import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  IconButton,
  Tooltip,
  Divider,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Close, FileCopy, Note, AutorenewOutlined } from "@material-ui/icons";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    paddingBottom: 0,
  },
  titleText: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    fontWeight: "bold",
    fontSize: "1.1rem",
    color: theme.palette.primary.main,
  },
  content: {
    minHeight: 200,
    padding: theme.spacing(2, 3),
  },
  summaryBox: {
    backgroundColor: theme.mode === "light" ? "#f9f9f9" : "#1e1e1e",
    borderRadius: 8,
    padding: theme.spacing(2),
    whiteSpace: "pre-wrap",
    fontSize: "0.9rem",
    lineHeight: 1.7,
    color: theme.mode === "light" ? "#333" : "#eee",
    border: `1px solid ${theme.palette.divider}`,
    maxHeight: 420,
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  loadingBox: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 200,
    gap: theme.spacing(2),
  },
  actions: {
    padding: theme.spacing(1, 3, 2),
    gap: theme.spacing(1),
    display: "flex",
    justifyContent: "flex-end",
  },
  spinIcon: {
    animation: "$spin 1s linear infinite",
  },
  "@keyframes spin": {
    "0%": { transform: "rotate(0deg)" },
    "100%": { transform: "rotate(360deg)" },
  },
}));

// Renderiza markdown simples (negrito)
const renderMarkdown = (text) => {
  if (!text) return null;
  return text.split("\n").map((line, i) => {
    const parts = line.split(/\*\*(.*?)\*\*/g);
    return (
      <span key={i}>
        {parts.map((part, j) =>
          j % 2 === 1 ? <strong key={j}>{part}</strong> : part
        )}
        {"\n"}
      </span>
    );
  });
};

const TicketSummaryModal = ({ open, onClose, ticketId, onConfirmClose }) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState("");
  const [savingNote, setSavingNote] = useState(false);

  // modo "ao finalizar": onConfirmClose está definido
  const isClosingMode = Boolean(onConfirmClose);

  React.useEffect(() => {
    if (open && ticketId) {
      fetchSummary();
    } else {
      setSummary("");
    }
  }, [open, ticketId]);

  const fetchSummary = async () => {
    setLoading(true);
    setSummary("");
    try {
      const { data } = await api.get(`/groq/summarize/${ticketId}`);
      if (data.success) {
        setSummary(data.summary);
      } else {
        toastError(data.error || "Erro ao gerar resumo");
        onClose();
      }
    } catch (err) {
      toastError(err);
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(summary);
    toast.success("Resumo copiado!");
  };

  const handleSaveNote = async () => {
    setSavingNote(true);
    try {
      const { data } = await api.post(`/groq/summarize/${ticketId}/note`, { summary });
      if (data.success) {
        toast.success("Resumo salvo como nota!");
        onClose();
      } else {
        toastError(data.error);
      }
    } catch (err) {
      toastError(err);
    } finally {
      setSavingNote(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" scroll="paper">
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <span className={classes.titleText}>
          <AutorenewOutlined fontSize="small" />
          Resumo IA do Atendimento
        </span>
        <IconButton size="small" onClick={onClose}>
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent className={classes.content}>
        {loading ? (
          <div className={classes.loadingBox}>
            <CircularProgress size={36} />
            <Typography variant="body2" color="textSecondary">
              Gerando resumo com Groq AI...
            </Typography>
          </div>
        ) : (
          <div className={classes.summaryBox}>
            {renderMarkdown(summary)}
          </div>
        )}
      </DialogContent>
      {!loading && summary && (
        <DialogActions className={classes.actions}>
          <Button
            onClick={handleSaveNote}
            disabled={savingNote}
            startIcon={savingNote ? <CircularProgress size={16} /> : <Note />}
            variant="outlined"
            size="small"
          >
            Salvar como Nota
          </Button>
          <Button
            onClick={handleCopy}
            startIcon={<FileCopy />}
            variant="outlined"
            size="small"
          >
            Copiar
          </Button>
          {isClosingMode ? (
            <Button
              onClick={() => { onConfirmClose(); onClose(); }}
              variant="contained"
              color="primary"
              size="small"
            >
              Finalizar Ticket
            </Button>
          ) : (
            <Button onClick={onClose} variant="contained" color="primary" size="small">
              Fechar
            </Button>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default TicketSummaryModal;
